import {
  mapCampaignsToTooltips,
} from '@/constants/filterCriteria/callingStatusOptions';
import FormWithFetchAutocompleteByIdTFC from '@components/LoadSearchTemplateDialog/templateFormControls/FetchAutocompleteByIdTFC';
import SelectEmailTFC from '@components/LoadSearchTemplateDialog/templateFormControls/SelectEmailTFC';
import SelectTFC from '@components/LoadSearchTemplateDialog/templateFormControls/SelectTFC';
import { FieldMetaData } from '@components/LoadSearchTemplateDialog/templateFormControls/types';

import type {
  MappedTemplateFormControlMapDeps,
  TemplateFormControlMap,
} from '@components/LoadSearchTemplateDialog/mappedTemplateFormControl/types';

export function buildCoreAndMembershipControls(
  deps: MappedTemplateFormControlMapDeps
): Partial<TemplateFormControlMap> {
  const {
    lookupGeometries,
    lookupGroups,
    lookupLists,
    lookupTags,
    lookupBranches,
    lookupMailings,
    lookupCatCampaigns,
    lookupLists: lookupListsForCalling,
    initialValue,
    simpleInitialValue,
  } = deps;

  return {
    geometryIds: ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Add location(s)'}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupGeometries}
        initialValue={initialValue}
      />
    ),
    groupIds: ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Add group(s)'}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupGroups}
        initialValue={initialValue}
      />
    ),
    listIds: ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Add list(s)'}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupLists}
        initialValue={initialValue}
      />
    ),
    tagIds: ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Add tag(s)'}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupTags}
        initialValue={initialValue}
      />
    ),
    hasPhone: ({ onChildChange }) => (
      <SelectTFC
        label={'Has phone type'}
        onChange={onChildChange as (value: string, fieldMetaData: FieldMetaData) => void}
        isBlankValid={false}
        selectMenuItems={[
          { title: '\u00a0', value: '' },
          { value: 'any', title: 'Any' },
          { value: 'mobile', title: 'Mobile' },
          { value: 'landline', title: 'Landline' },
        ]}
        initialValue={simpleInitialValue as string}
      />
    ),
    hasEmail: ({ onChildChange }) => (
      <SelectEmailTFC
        label={'Is emailable'}
        onChange={onChildChange as (value: boolean, fieldMetaData: FieldMetaData) => void}
        initialValue={simpleInitialValue as boolean}
      />
    ),
    'membership.branchIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Select a branch (optional)'}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupBranches}
        isEmptyValid
        initialValue={initialValue}
      />
    ),
    'membership.notRenewing': ({ onChildChange }) => (
      <SelectTFC
        label={'Filter by Not Renewing'}
        onChange={onChildChange as (value: string, fieldMetaData: FieldMetaData) => void}
        isBlankValid={false}
        selectMenuItems={[
          { title: '\u00a0', value: '' },
          { value: 'exclude', title: 'Exclude' },
          { value: 'onlyInclude', title: 'Only include' },
        ]}
        initialValue={simpleInitialValue as string}
      />
    ),
    'mailing.mailingIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Add a mailing'}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupMailings}
        initialValue={initialValue}
      />
    ),
    'mailing.mailingStatus': ({ onChildChange }) => (
      <SelectTFC
        label={'Mailing status'}
        onChange={onChildChange as (value: string, fieldMetaData: FieldMetaData) => void}
        isBlankValid={false}
        variant="filled"
        selectMenuItems={[
          { title: '\u00a0', value: '' },
          { value: 'delivered', title: 'Delivered' },
          { value: 'unopened', title: 'Unopened' },
          { value: 'opened', title: 'Opened' },
        ]}
        initialValue={simpleInitialValue as string}
      />
    ),
    'callingStatus.callingStatusListIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Contacted in list(s) (optional)'}
        onChange={onChildChange}
        minLength={3}
        lookup={lookupListsForCalling}
        isEmptyValid
        initialValue={initialValue}
      />
    ),
    'callingStatus.callingStatusCampaignIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByIdTFC
        label={'Contacted by campaign(s) (optional)'}
        onChange={onChildChange}
        minLength={3}
        lookup={async (lookupValue: string) => {
          const campaigns = await lookupCatCampaigns(lookupValue);
          return campaigns.map((x) => mapCampaignsToTooltips(x));
        }}
        isEmptyValid
        initialValue={initialValue}
      />
    ),
  };
}
